import React, { useRef } from 'react';
import { Box, Checkbox, Divider, FormControlLabel, Grid, Typography } from '@mui/material';
import LocationFeatures from '../../../components/LocationFeatures';
import { useFeatureFlags } from '../../../hooks/routing/useFeatureFlags';
import FeatureSubPanel from '../components/FeatureSubPanel';
import { getFeaturesByCategory } from '../featureRegistry';
import { setFeatureDetail, toggleFeature } from '../featureSelection';
import type { LocationFeatureDetails } from '../../../types/Location';
import { LocationWizardData } from '../types';

interface LocationFeaturesStepProps {
  data: LocationWizardData;
  updateData: (partial: Partial<LocationWizardData>) => void;
}

/** Features shown in step 2 and managed there — exclude from this grid. */
const EXCLUDED_FEATURES = new Set(['24/7 Access']);

const FEATURE_OPTIONS = LocationFeatures.OPTIONS.filter(
  (f) => !EXCLUDED_FEATURES.has(f)
);

/**
 * Explicit two-column distribution per design spec (7 left, 4 right).
 * Items not in either list fall back to the left column.
 */
const LEFT_COLUMN_FEATURES = [
  'Manned Security',
  'Fenced / Gated',
  'Yard Cameras',
  'Gate Cameras',
  'Lights',
  'M & R Offered',
  'Overnight Truck Parking',
];

const RIGHT_COLUMN_FEATURES = [
  'Paved',
  'Daily Inventory Report',
  'Side-pick / Top-Pick',
  'Restrooms',
];

/**
 * Split feature options into left/right columns using the explicit design order.
 *
 * Any option present in `LocationFeatures.OPTIONS` but missing from both column
 * arrays (e.g. a newly added feature, or one renamed so it no longer matches) is
 * appended to the left column instead of silently disappearing from the UI. A
 * dev-only warning flags the stale mapping so it can be corrected.
 */
const leftFeatures = LEFT_COLUMN_FEATURES.filter((f) => FEATURE_OPTIONS.includes(f));
const rightFeatures = RIGHT_COLUMN_FEATURES.filter((f) => FEATURE_OPTIONS.includes(f));

const mappedFeatures = new Set([...leftFeatures, ...rightFeatures]);
const unmappedFeatures = FEATURE_OPTIONS.filter((f) => !mappedFeatures.has(f));

if (unmappedFeatures.length > 0) {
  // Append rather than drop, so the supplier can always toggle every feature.
  leftFeatures.push(...unmappedFeatures);
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(
      `LocationFeaturesStep: features not assigned to a column (rendered in the left column by default): ${unmappedFeatures.join(
        ', '
      )}. Update LEFT_COLUMN_FEATURES / RIGHT_COLUMN_FEATURES.`
    );
  }
}

/**
 * Step 4 (Features) of the Create/Edit Location wizard.
 *
 * Renders a two-column checkbox grid of location features (security, cameras,
 * paving, etc.). The "24/7 Access" feature is excluded because it is already
 * captured in Step 2 (Location Settings).
 */
const LocationFeaturesStep = (props: LocationFeaturesStepProps) => {
  const { data, updateData } = props;
  const { locationFeatureDetails } = useFeatureFlags();
  const features = data.locationFeatures || [];
  // Details of features unchecked while Step 4 is mounted, restored if the supplier re-checks them.
  const uncheckedDetails = useRef(new Map<string, LocationFeatureDetails>());

  const selectedFeatures = new Set(
    features.map((f) => f.feature).filter(Boolean)
  );

  const onToggle = (feature: string) => {
    const current = features.find((f) => f.feature === feature);
    let restore: LocationFeatureDetails | undefined;
    if (current) {
      // Replace or clear: an older entry is stale once the feature was re-added (e.g. by Autofill).
      if (current.details && Object.keys(current.details).length > 0) {
        uncheckedDetails.current.set(feature, current.details);
      } else {
        uncheckedDetails.current.delete(feature);
      }
    } else {
      restore = uncheckedDetails.current.get(feature);
      uncheckedDetails.current.delete(feature);
    }
    updateData({ locationFeatures: toggleFeature(features, feature, locationFeatureDetails, restore) });
  };

  if (locationFeatureDetails) {
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Location Features
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select the features available at this location. Add details where you can — buyers see them on your listing.
        </Typography>

        {/* Stacked on mobile/tablet, three category columns on desktop. */}
        <Grid container columnSpacing={4} rowSpacing={3}>
          {getFeaturesByCategory(true).map(({ category, features: definitions }) => {
            const selectedCount = definitions.filter((def) => selectedFeatures.has(def.feature)).length;
            return (
              <Grid item xs={12} lg={4} key={category} component="section" aria-labelledby={`feature-category-${category}`}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    id={`feature-category-${category}`}
                    variant="subtitle2"
                    component="h3"
                    sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}
                  >
                    {category}
                  </Typography>
                  {selectedCount > 0 && (
                    <Typography variant="caption" color="primary">
                      {selectedCount} selected
                    </Typography>
                  )}
                </Box>
                <Divider sx={{ mt: 0.5, mb: 1 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {definitions.map((def) => {
                    const checked = selectedFeatures.has(def.feature);
                    const selected = features.find((f) => f.feature === def.feature);
                    return (
                      <React.Fragment key={def.feature}>
                        <FormControlLabel
                          control={<Checkbox checked={checked} onChange={() => onToggle(def.feature)} />}
                          label={def.label ?? def.feature}
                        />
                        {checked && def.subFields.length > 0 && (
                          <FeatureSubPanel
                            definition={def}
                            details={selected?.details}
                            onChange={(key, value) =>
                              updateData({ locationFeatures: setFeatureDetail(features, def.feature, key, value) })
                            }
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Location Features
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select which features are available at this location.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          columnGap: 3,
          rowGap: 0.5,
        }}
      >
        {/* Left column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {leftFeatures.map((feature) => (
            <FormControlLabel
              key={feature}
              control={
                <Checkbox
                  checked={selectedFeatures.has(feature)}
                  onChange={() => onToggle(feature)}
                />
              }
              label={feature}
            />
          ))}
        </Box>

        {/* Right column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {rightFeatures.map((feature) => (
            <FormControlLabel
              key={feature}
              control={
                <Checkbox
                  checked={selectedFeatures.has(feature)}
                  onChange={() => onToggle(feature)}
                />
              }
              label={feature}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default LocationFeaturesStep;
